import type { LoadedTranslations, TranslationNamespace } from '@autoplan/common'
import { defineQuery } from 'groq'
import { getClient, isPreview } from '../client'

type NamespaceState = {
  translations: LoadedTranslations | null
  settled: boolean
  pending: Promise<LoadedTranslations | null> | null
  generation: number
}

export function getTranslations(namespace: TranslationNamespace): LoadedTranslations | null {
  return stateOf(namespace).translations
}

export function isSettled(namespace: TranslationNamespace): boolean {
  return stateOf(namespace).settled
}

export function subscribeToTranslations(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function loadTranslations(namespace: TranslationNamespace, force = false): Promise<LoadedTranslations | null> {
  const state = stateOf(namespace)
  if (state.pending && !force) return state.pending

  const request = ++state.generation
  state.pending = getClient()
    .withConfig({ useCdn: !isPreview() })
    .fetch(TRANSLATIONS_QUERY, { prefix: `${namespace}.` }, { stega: false })
    .then((result) => {
      if (request !== state.generation) return state.translations
      state.translations = Object.fromEntries(result.map(({ key, nb, en }) => [key, { nb, en }]))
      return state.translations
    })
    .catch((error) => {
      console.warn(`Could not load ${namespace} translations from Sanity`, error)
      if (request === state.generation) state.pending = null
      return state.translations
    })
    .then((result) => {
      settle(state)
      return result
    })

  return state.pending
}

export function preloadTranslations(namespace: TranslationNamespace): Promise<void> {
  const state = stateOf(namespace)
  const load = loadTranslations(namespace).then(() => undefined)
  if (state.translations) return Promise.resolve()

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      settle(state)
      resolve()
    }, PRELOAD_TIMEOUT_MS)
  })

  return Promise.race([load.then(() => clearTimeout(timer)), timeout])
}

const TRANSLATIONS_QUERY = defineQuery(
  `*[_type == "translation" && (string::startsWith(key, "common.") || string::startsWith(key, $prefix))]{ key, nb, en }`,
)
const PRELOAD_TIMEOUT_MS = 10000

const states = new Map<TranslationNamespace, NamespaceState>()
const listeners = new Set<() => void>()

function stateOf(namespace: TranslationNamespace): NamespaceState {
  let state = states.get(namespace)
  if (!state) {
    state = { translations: null, settled: false, pending: null, generation: 0 }
    states.set(namespace, state)
  }
  return state
}

function settle(state: NamespaceState) {
  state.settled = true
  for (const listener of listeners) listener()
}
